import { inject, injectable } from "inversify";
import * as IHttpService from "@modules/infra/http/models/IHttpService";
import { Types } from "@ioc/types";
import {
  INotificationService,
  INotificationFilter,
  IPaginatedNotifications,
  INotificationCreateDTO,
  INotificationUploadResponse,
  INotificationRead,
  IUserNotificationWithDetails,
  INotification,
  ISegment,
} from "../model/INotifications";

@injectable()
export class NotificationService implements INotificationService {
  @inject(Types.IHttpService)
  private httpInstance!: IHttpService.IHttpService;

  public async getNotificationsByUser(
    readStatus = "ALL"
  ): Promise<IUserNotificationWithDetails[]> {
    try {
      const response = await this.httpInstance.get(
        "/notification-users/user/current",
        {
          params: { readStatus },
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  public async searchNotifications(
    params: INotificationFilter
  ): Promise<IPaginatedNotifications> {
    try {
      const response = await this.httpInstance.get("/notifications/search", {
        params,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  public async uploadNotificationImage(
    file: File
  ): Promise<INotificationUploadResponse> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await this.httpInstance.post(
        "/notifications/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  public async createNotification(
    data: INotificationCreateDTO
  ): Promise<INotification> {
    try {
      const response = await this.httpInstance.post("/notifications", data);
      return response;
    } catch (error) {
      throw error;
    }
  }

  public async updateNotification(
    id: string,
    data: INotificationCreateDTO
  ): Promise<INotification> {
    try {
      const response = await this.httpInstance.put(
        `/notifications/${id}`,
        data
      );
      return response?.[0];
    } catch (error) {
      throw error;
    }
  }

  public async deleteNotification(id: string): Promise<void> {
    try {
      await this.httpInstance.delete(`/notifications/${id}`);
    } catch (error) {
      throw error;
    }
  }

  public async readNotification(id: string): Promise<INotificationRead[]> {
    try {
      const response = await this.httpInstance.patch(
        `/notification-users/${id}/read`
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  public async unreadNotification(id: string): Promise<void> {
    try {
      await this.httpInstance.patch(`/notification-users/${id}/unread`);
    } catch (error) {
      throw error;
    }
  }

  public async getUserNotificationById(
    id: string
  ): Promise<IUserNotificationWithDetails> {
    try {
      const response = await this.httpInstance.get(`/notification-users/${id}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  public async getNotificationById(id: string): Promise<INotification> {
    try {
      const response = await this.httpInstance.get(`/notifications/${id}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  public async getSegments(): Promise<ISegment[]> {
    try {
      const response = await this.httpInstance.get("/filters?action=segment");
      return response;
    } catch (error) {
      throw error;
    }
  }
}
